import React from "react";
import {Button, Modal, UploadProps} from "antd";
import {InboxOutlined, UploadOutlined} from "@ant-design/icons";
import {Trans} from "@lingui/macro";
import Dragger from "antd/es/upload/Dragger";
import ButtonWithIcon from "../components/buttons/button-with-icon";
import {openNotification} from "../utils/openNotification";
import {useAxios} from "../utils/hooks";
import useSafeState from "../utils/safe-state";

interface Interface {
    onSuccess?: () => void;
}

const getDetailedErrorMessage = (error: any): string => {
    if (error && error.response && error.response.data) {
        const data = error.response.data;
        
        if (typeof data.message === 'string') {
            return data.message;
        }
        
        if (typeof data.error === 'string') {
            return data.error;
        }
        
        if (typeof data === 'string') {
            return data;
        }
    }
    
    if (error && error.message) {
        return error.message;
    }
    
    return 'An unexpected error occurred. Please try again.';
};

const UploadLocationExcelForm = (props: Interface) => {
    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const [locationFileList, setLocationFileList] = useSafeState<Array<any>>([]);
    const [locationLoading, setLocationLoading] = useSafeState(false);

    const handleDownloadTemplate = () => {
        if (axiosInstance.current) {
            axiosInstance.current
                .get('/resource/locations/download-template', {
                    responseType: 'blob',
                })
                .then((response) => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'location_upload_template.xlsx');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    openNotification('success', 'Template downloaded successfully');
                })
                .catch((error) => {
                    openNotification('error', getDetailedErrorMessage(error));
                });
        }
    };

    const locationProps: UploadProps = {
        name: 'file',
        multiple: false,
        fileList: locationFileList,
        accept: '.xlsx',
        onChange(info) {
            if (info.file.status !== 'uploading') {
                console.log(info.file, info.fileList);
            }
        },
        beforeUpload(file: any) {
            let fileList2: Array<any> = locationFileList;
            fileList2.push(file);
            setLocationFileList(fileList2);
            return false;
        },
    };

    const handleUploadLocationExcel = () => {
        if (!locationFileList || locationFileList.length === 0) {
            openNotification('error', 'No file selected. Please select a file to upload.');
            return;
        }

        if (axiosInstance.current != null) {
            setLocationLoading(true);
            const formData = new FormData();
            locationFileList.forEach(file => {
                formData.append('file', file);
            });

            axiosInstance.current
                .post('/resource/locations/preview-xls-with-source', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then((previewResponse) => {
                    const preview = previewResponse.data;
                    
                    if (preview.updatedLocations > 0) {
                        let updateMessage = `Upload Summary:\n\n`;
                        updateMessage += `• ${preview.newLocations} new location(s) will be added\n`;
                        updateMessage += `• ${preview.updatedLocations} existing location(s) will be updated\n\n`;
                        
                        if (preview.updates && preview.updates.length > 0) {
                            updateMessage += `The following locations will be renamed:\n\n`;
                            preview.updates.forEach((update: any) => {
                                updateMessage += `  "${update.oldName}" → "${update.newName}"\n`;
                                updateMessage += `  Reason: ${update.reason}\n\n`;
                            });
                        }
                        
                        updateMessage += `\nDo you want to proceed with the upload?`;
                        
                        Modal.confirm({
                            title: 'Confirm Location Upload',
                            content: <pre style={{whiteSpace: 'pre-wrap'}}>{updateMessage}</pre>,
                            onOk: () => {
                                const confirmedFormData = new FormData();
                                locationFileList.forEach(file => {
                                    confirmedFormData.append('file', file);
                                });
                                confirmedFormData.append('confirmed', 'true');
                                
                                return axiosInstance.current!
                                    .post('/resource/locations/upload-xls-with-source', confirmedFormData, {
                                        headers: { 'Content-Type': 'multipart/form-data' },
                                    })
                                    .then((response) => {
                                        const message = response.data || 'Locations successfully uploaded';
                                        openNotification('customSuccess', message);
                                        setLocationFileList([]);
                                        if (props.onSuccess) {
                                            props.onSuccess();
                                        }
                                    })
                                    .catch(function (error) {
                                        openNotification('error', getDetailedErrorMessage(error));
                                    })
                                    .finally(() => {
                                        setLocationLoading(false);
                                    });
                            },
                            onCancel: () => {
                                setLocationLoading(false);
                                setLocationFileList([]);
                            }
                        });
                    } else {
                        const confirmedFormData = new FormData();
                        locationFileList.forEach(file => {
                            confirmedFormData.append('file', file);
                        });
                        confirmedFormData.append('confirmed', 'true');

                        axiosInstance.current!
                            .post('/resource/locations/upload-xls-with-source', confirmedFormData, {
                                headers: { 'Content-Type': 'multipart/form-data' },
                            })
                            .then((response) => {
                                const message = response.data || 'Locations successfully uploaded';
                                openNotification('customSuccess', message);
                                setLocationFileList([]);
                                if (props.onSuccess) {
                                    props.onSuccess();
                                }
                            })
                            .catch(function (error) {
                                openNotification('error', getDetailedErrorMessage(error));
                            })
                            .finally(() => {
                                setLocationLoading(false);
                            });
                    }
                })
                .catch(function (error) {
                    openNotification('error', getDetailedErrorMessage(error));
                    setLocationLoading(false);
                })
                .finally(() => {
                    setLocationFileList([]);
                });
        }
    };

    return (
        <div>
            <p><Trans>Download the Excel template below, fill in your location data, and upload it. The template includes the required column headers and an example row.</Trans></p>
            
            <div style={{ marginBottom: 16 }}>
                <Button type="default" onClick={handleDownloadTemplate}>
                    <ButtonWithIcon
                        icon={<UploadOutlined style={{ transform: 'rotate(180deg)' }} />}
                        text={<Trans>Download Template</Trans>}
                    />
                </Button>
            </div>

            <h3><Trans>Required columns in the Excel file:</Trans></h3>
            <ul>
                <li><strong>LOCATION NAME</strong> - <Trans>Name of the location</Trans></li>
                <li><strong>LOCATION GEOJSON</strong> - <Trans>WKT MULTIPOLYGON format (e.g., MULTIPOLYGON (((lng lat, lng lat, ...))))</Trans></li>
                <li><strong>SOURCE</strong> - <Trans>Either 'FINISHED_PRODUCT' or 'RAW_MATERIAL'</Trans></li>
            </ul>

            <Dragger {...locationProps}>
                <div></div>
                <p className="ant-upload-drag-icon">
                    <InboxOutlined/>
                </p>
                <p className="ant-upload-text">
                    <Trans>Click or drag file to this area to upload</Trans>
                </p>
                <p className="ant-upload-hint">
                    <Trans>
                        Support for a single upload. Only .xlsx files are accepted.
                    </Trans>
                </p>
            </Dragger>
            <div className="center-card" style={{marginTop: 10}}>
                <Button loading={locationLoading} htmlType="button" type="primary"
                        onClick={() => handleUploadLocationExcel()}>
                    <ButtonWithIcon
                        icon={<UploadOutlined/>}
                        text={<Trans>Excel upload</Trans>}
                    />
                </Button>
            </div>
        </div>
    );
};

export default UploadLocationExcelForm;
